import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type JSX, useEffect, useMemo, useState } from "react";
import { defaultWebStudioScope } from "../api/boot";
import { WebStudioCloudApiClient } from "../api/generatedClient";
import { buildMutationEnvelope } from "../api/mutationEnvelope";
import { createWebStudioFetchTransport } from "../api/transport";
import {
  createWorkspaceFromTemplate,
  type WebStudioTemplateCatalogSnapshot,
  type WebStudioWorkspaceCatalogSnapshot,
  workspaceSelectionOutcome,
} from "../api/workspaces";
import {
  templateCatalogQueryOptions,
  workspaceCatalogQueryOptions,
} from "../app/queries/workspaces";
import { useBootRefresh } from "../app/refresh";
import { scopeFromSearch } from "../app/scope";
import { useScopeSwitch } from "../app/scopeSwitch";
import { useShellInspector } from "../app/shellInspector";
import { useBillingOverview, useBootRead } from "../app/useBootRead";
import { Button } from "../design";
import { FactsSurface } from "../features/facts";
import {
  BillingSection,
  type CreateFromTemplateState,
  type HomeCatalogView,
  OnboardingCatalog,
  OverviewSummary,
  SystemPanel,
} from "../features/home";

export const Route = createFileRoute("/")({
  component: HomeRoute,
});

const BROWSER_ACTOR = { actorKind: "browser_client", actorId: "web-studio-browser" } as const;

function catalogView<Snapshot>(query: {
  data: Snapshot | undefined;
  isError: boolean;
  error: unknown;
}): HomeCatalogView<Snapshot> {
  if (query.data !== undefined) {
    return { phase: "ready", snapshot: query.data };
  }
  if (query.isError) {
    return {
      phase: "failed",
      snapshot: null,
      errorMessage: query.error instanceof Error ? query.error.message : String(query.error),
    };
  }
  return { phase: "loading", snapshot: null };
}

function HomeRoute(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const scope = useMemo(() => scopeFromSearch(search), [search]);
  const read = useBootRead(scope);
  const billing = useBillingOverview(scope);
  const { token } = useBootRefresh();
  const client = useMemo(() => new WebStudioCloudApiClient(createWebStudioFetchTransport()), []);
  const scopeSwitch = useScopeSwitch();
  const inspector = useShellInspector();
  const [focusedFactRowId, setFocusedFactRowId] = useState<string | null>(null);
  const [browseRequested, setBrowseRequested] = useState(false);
  const [createState, setCreateState] = useState<CreateFromTemplateState>({ phase: "idle" });

  const boot = read.kind === "ready" ? read.boot : null;
  const factsPlane = read.kind === "ready" ? read.factsPlane : null;

  const workspacesQuery = useQuery(
    workspaceCatalogQueryOptions(client, scope, { generation: token }),
  );
  const templatesQuery = useQuery(
    templateCatalogQueryOptions(client, scope, { generation: token }),
  );

  // The shell inspector dock shows the boot route-state section by default
  // on the Overview: selection context without changing route or filters.
  const routeStateSection = boot?.data.routeStateSection;
  const publishSelection = inspector.publish;
  useEffect(() => {
    if (routeStateSection === undefined) {
      return;
    }
    publishSelection({
      title: routeStateSection.label,
      subtitle: "web_studio_boot",
      sectionLabel: "Backend route state",
      rows: routeStateSection.rows.map((row) => ({
        label: row.rowId,
        value: row.valueSummary ?? row.label,
      })),
    });
  }, [routeStateSection, publishSelection]);

  const createTargetDigest =
    workspacesQuery.data?.responseIdentity.snapshotIdentity ??
    boot?.responseIdentity.snapshotIdentity ??
    "sha256:web-studio-workspace-catalog-unread";

  const createMutation = useMutation({
    mutationFn: (vars: { templateId: string; templateLabel: string; workspaceName: string }) =>
      createWorkspaceFromTemplate(
        client,
        scope,
        buildMutationEnvelope({
          ...BROWSER_ACTOR,
          expectedState: { targetDigest: createTargetDigest },
        }),
        { templateId: vars.templateId, workspaceName: vars.workspaceName },
      ),
    onSuccess: (response, vars) => {
      const primary = response.diagnostics[0]?.diagnostic;
      if (primary !== undefined && primary.severity === "error") {
        setCreateState({
          phase: "failed",
          templateId: vars.templateId,
          templateLabel: vars.templateLabel,
          workspaceName: vars.workspaceName,
          code: primary.code,
          message: primary.message,
        });
        return;
      }
      const outcome = workspaceSelectionOutcome(response);
      setCreateState({
        phase: "created",
        workspaceName: vars.workspaceName,
        response,
        confirmedWorkspaceId: outcome.kind === "confirmed" ? outcome.value : null,
      });
    },
    onError: (error, vars) => {
      setCreateState({
        phase: "failed",
        templateId: vars.templateId,
        templateLabel: vars.templateLabel,
        workspaceName: vars.workspaceName,
        code: "web_studio.workspace.create_transport_failed",
        message: error instanceof Error ? error.message : String(error),
      });
    },
  });

  const workspaceUnselected = scope.workspaceId === defaultWebStudioScope().workspaceId;
  const catalogTransition = workspacesQuery.data?.data.transitionState;
  const firstRunIndicated = catalogTransition !== undefined && catalogTransition !== "loaded";
  const showOnboarding =
    browseRequested || workspaceUnselected || firstRunIndicated || createState.phase !== "idle";

  const onSubmitCreate = (workspaceName: string): void => {
    if (createState.phase !== "drafting" && createState.phase !== "failed") {
      return;
    }
    const { templateId, templateLabel } = createState;
    setCreateState({ phase: "pending", templateId, templateLabel, workspaceName });
    createMutation.mutate({ templateId, templateLabel, workspaceName });
  };

  const goTo = (to: string): void => {
    void navigate({ to, search: (previous) => previous });
  };

  return (
    <>
      {boot === null && read.kind === "loading" ? (
        <p className="muted" role="status">
          Loading the workspace overview from the backend…
        </p>
      ) : null}
      {boot === null ? null : <OverviewSummary boot={boot} onNavigate={goTo} />}
      {showOnboarding ? (
        <OnboardingCatalog
          workspaces={catalogView<WebStudioWorkspaceCatalogSnapshot>(workspacesQuery)}
          templates={catalogView<WebStudioTemplateCatalogSnapshot>(templatesQuery)}
          create={createState}
          switchPending={scopeSwitch.pending}
          onStartCreate={(templateId, templateLabel) =>
            setCreateState({ phase: "drafting", templateId, templateLabel })
          }
          onCancelCreate={() => setCreateState({ phase: "idle" })}
          onSubmitCreate={onSubmitCreate}
          onSelectWorkspace={(workspaceId) =>
            scopeSwitch.requestScopeSwitch("workspace", workspaceId)
          }
        />
      ) : (
        <section className="home-browse" aria-label="Workspace onboarding">
          <Button variant="ghost" onClick={() => setBrowseRequested(true)}>
            Browse workspaces and templates
          </Button>
        </section>
      )}
      <FactsSurface
        factsPlane={factsPlane}
        focusedRowId={focusedFactRowId}
        onFocusRow={setFocusedFactRowId}
      />
      <BillingSection billing={billing} />
      <SystemPanel
        boot={boot}
        phase={read.kind}
        protectedStateCleared={read.kind === "ready" && read.protectedStateCleared}
        errorMessage={read.kind === "failed" ? read.message : undefined}
        deferredRows={boot?.data.deferredPanelRows}
      />
    </>
  );
}
